import { useMemo } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Download, ExternalLink, Receipt } from "lucide-react";
import { formatCurrency } from "@/lib/format";
import { format } from "date-fns";
import {
  useGetPayments,
  type Payment,
  type Receipt as ReceiptType,
} from "@/api/payments";

interface ReceiptWithPayment {
  receipt: ReceiptType;
  payment: Payment;
}

export function ReceiptsTable() {
  const { data: payments, isLoading } = useGetPayments({});

  // Extract all receipts from payments
  const receiptsWithPayments = useMemo(() => {
    if (!payments) return { customReceipts: [], stripeReceipts: [] };

    const customReceipts: ReceiptWithPayment[] = [];
    const stripeReceipts: { payment: Payment; isStripe: true }[] = [];

    payments.forEach((payment) => {
      // Custom receipts (CASH, E_TRANSFER, WECHAT)
      if (payment.receipts && payment.receipts.length > 0) {
        payment.receipts.forEach((receipt) => {
          customReceipts.push({ receipt, payment });
        });
      }

      // Stripe receipts (SAVED_CARD, INVOICE)
      if (
        (payment.method === "SAVED_CARD" || payment.method === "INVOICE") &&
        (payment.status === "PAID" ||
          payment.status === "REFUNDED" ||
          payment.status === "PARTIAL_REFUND")
      ) {
        stripeReceipts.push({ payment, isStripe: true });
      }
    });

    return { customReceipts, stripeReceipts };
  }, [payments]);

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Receipts</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {[1, 2, 3, 4, 5].map((i) => (
              <Skeleton key={i} className="h-12 w-full" />
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  const { customReceipts, stripeReceipts } = receiptsWithPayments;
  const totalReceipts = customReceipts.length + stripeReceipts.length;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Receipts ({totalReceipts})</CardTitle>
      </CardHeader>
      <CardContent>
        {totalReceipts === 0 ? (
          <div className="text-center p-8 text-muted-foreground">
            <Receipt className="mx-auto h-12 w-12 mb-2 opacity-50" />
            <p>No receipts found</p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Receipt Number</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Payment Method</TableHead>
                <TableHead>Amount</TableHead>
                <TableHead>Order</TableHead>
                <TableHead>Date</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {/* Custom Receipts */}
              {customReceipts.map(
                ({ receipt, payment }: ReceiptWithPayment) => (
                  <TableRow key={receipt.id}>
                    <TableCell className="font-medium">
                      {receipt.receiptNumber}
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant={
                          receipt.type === "REFUND" ? "destructive" : "default"
                        }
                      >
                        {receipt.type}
                      </Badge>
                    </TableCell>
                    <TableCell>{payment.method}</TableCell>
                    <TableCell>{formatCurrency(payment.amountCents)}</TableCell>
                    <TableCell>
                      {payment.order ? `ORD-${payment.order.orderNumber}` : "-"}
                    </TableCell>
                    <TableCell>
                      {format(
                        new Date(receipt.createdAt),
                        "MMM d, yyyy h:mm a"
                      )}
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => window.open(receipt.fileUrl, "_blank")}
                      >
                        <Download className="h-4 w-4 mr-2" />
                        Download
                      </Button>
                    </TableCell>
                  </TableRow>
                )
              )}

              {/* Stripe Receipts */}
              {stripeReceipts.map(
                ({ payment }: { payment: Payment; isStripe: true }) => (
                  <TableRow key={`stripe-${payment.id}`}>
                    <TableCell className="font-medium">
                      Stripe Receipt
                    </TableCell>
                    <TableCell>
                      <Badge variant="secondary">
                        {payment.status === "REFUNDED" ||
                        payment.status === "PARTIAL_REFUND"
                          ? "REFUND"
                          : "PAYMENT"}
                      </Badge>
                    </TableCell>
                    <TableCell>{payment.method}</TableCell>
                    <TableCell>{formatCurrency(payment.amountCents)}</TableCell>
                    <TableCell>
                      {payment.order ? `ORD-${payment.order.orderNumber}` : "-"}
                    </TableCell>
                    <TableCell>
                      {format(
                        new Date(payment.createdAt),
                        "MMM d, yyyy h:mm a"
                      )}
                    </TableCell>
                    <TableCell>
                      {payment.stripeReceiptUrl ? (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() =>
                            window.open(payment.stripeReceiptUrl, "_blank")
                          }
                        >
                          <Download className="h-4 w-4 mr-2" />
                          Download
                        </Button>
                      ) : payment.stripeChargeId ? (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() =>
                            window.open(
                              `https://dashboard.stripe.com/payments/${payment.stripeChargeId}`,
                              "_blank"
                            )
                          }
                        >
                          <ExternalLink className="h-4 w-4 mr-2" />
                          View in Stripe
                        </Button>
                      ) : null}
                    </TableCell>
                  </TableRow>
                )
              )}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}
